import { dia } from "@clientio/rappid";
import * as joint from "@clientio/rappid";
import { State, StateView } from "./mvc/state";
import { Ref, ref } from "vue";
import * as ace from "ace-builds";
import { Transition, TransitionView } from "./mvc/transition";
import { Region } from "./mvc/region";
import { UIEventBus } from "../event-bus";

import { isInActiveSubdiagram } from "./mvc/base/element-controller";
import RappidContext from "./rappid/rappid-context";
import type { SubdiagramBreadcrumbItem } from "./types";
import { BaseView } from "./mvc/base/base-node";

export const activeView: Ref<StateView | null> = ref(null);

export function updateVisibleStates(context: RappidContext, stateView: StateView | null) {
  context.commandManager.stopListening();
  activeView.value = stateView;
  context.paper.removeTools();
  UIEventBus.emit("cellSelectionChanged", new joint.mvc.Collection<dia.Cell>());
  context.mouseNavigationController?.trackNavigation(stateView);
  if (activeView.value === null) {
    viewTopLevel(context.paper);
  } else {
    viewSubdiagram(context.paper);
  }
  context.commandManager?.listen();
}

function viewSubdiagram(paper: dia.Paper) {
  hideAll(paper);
  const cells = getSubdiagramCells(activeView.value.model as State);
  cells.forEach((c) => {
    const view = paper.findViewByModel(c);
    showView(view);
  });
}

function viewTopLevel(paper: dia.Paper) {
  const topLevelStates = [];
  paper.model.getCells().forEach((cell) => {
    const view = paper.findViewByModel(cell);
    if (!view) return;
    if (isInActiveSubdiagram(cell)) {
      hideView(view);
    } else {
      showView(view);
      if (cell instanceof State) {
        (view as StateView).updateSubdiagramTool();
        topLevelStates.push(cell);
      }
    }
  });
  topLevelStates.forEach((state) => {
    showSelfTransition(state, paper);
  });
}

export function getSubdiagramCells(model: State): joint.dia.Cell[] {
  const result: joint.dia.Cell[] = [];

  function traverse(cell: joint.dia.Cell) {
    for (const child of cell.getEmbeddedCells()) {
      // Skip transitions that target back to the parent model
      if (child instanceof Transition && child.getTargetCell() === model) {
        continue;
      }

      result.push(child);

      // For states with subdiagrams, only include self-transitions
      if (child instanceof State && child.getSubdiagram()) {
        child
          .getEmbeddedCells()
          .filter((c) => isSelfTransition(c))
          .forEach((t) => result.push(t));
        continue;
      }

      // Otherwise, recursively traverse
      traverse(child);
    }
  }

  traverse(model);
  return result;
}

function isSelfTransition(cell: dia.Cell) {
  if (cell instanceof Transition) {
    if (cell.getTargetCell() === cell.getSourceCell()) {
      return true;
    }
  }
  return false;
}

function showSelfTransition(state: State, paper: dia.Paper) {
  state
    .getEmbeddedCells()
    .filter((c) => isSelfTransition(c))
    .forEach((t) => {
      const transitionView = paper.findViewByModel(t);
      showView(transitionView);
    });
}

function hideAll(paper: dia.Paper) {
  paper.model.getCells().forEach((cell) => {
    const view = paper.findViewByModel(cell);
    if (!view) return;
    hideView(view);
  });
}

function hideView(view: dia.ElementView | dia.LinkView) {
  view.model.attr("root/display", "none");
  if ("editor" in view && view.editor) {
    (view.editor as ace.Ace.Editor).container.style.display = "none";
  }
  disableEmbed(view);
  updateDiagnostics(view);
}

function showView(view: dia.ElementView | dia.LinkView) {
  view.model.attr("root/display", "");
  if ("editor" in view && view.editor) {
    (view.editor as ace.Ace.Editor).container.style.display = "";
  }
  if (view instanceof StateView) {
    view.updateSubdiagramTool();
  }
  enableEmbed(view);
  updateDiagnostics(view);
}

function disableEmbed(view: dia.ElementView | dia.LinkView) {
  const model = view.model;

  if (model instanceof State || model instanceof Region) {
    model.set({ embedable: false });
  }
}

function enableEmbed(view: dia.ElementView | dia.LinkView) {
  const model = view.model;

  if (model instanceof Region) {
    model.set({ embedable: true });
  } else if (model instanceof State) {
    model.set({ embedable: !model.getSubdiagram() });
  }
}

export function findSubdiagramParent(cell): dia.Cell | null {
  if (!isInActiveSubdiagram(cell)) {
    return null;
  }
  let parent = cell.getParentCell();
  while (parent) {
    if (parent instanceof State && parent.getSubdiagram()) {
      return parent;
    }
    parent = parent.getParentCell();
  }
  return null;
}

export function getSubdiagramBreadcrumbItems(): SubdiagramBreadcrumbItem[] {
  const items: SubdiagramBreadcrumbItem[] = [];
  let current = activeView.value;

  while (current) {
    const model = current.model;
    if (model instanceof State && model.getSubdiagram()) {
      items.push({
        title: model.getName() || "State",
        stateView: current,
      });
    }
    const parentCell = model.getParentCell?.();
    if (!parentCell) break;
    const parentView = current.paper?.findViewByModel(parentCell) as StateView | null;
    current = parentView;
  }

  items.push({ title: "Root", stateView: null });

  return items.reverse();
}

function updateDiagnostics(view: dia.ElementView | dia.LinkView) {
  if (view instanceof BaseView || view instanceof TransitionView) {
    view.displayDiagnostics();
  }
}
